import { ClientMsgKind, Phase } from '@infiltre/shared';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { selectEliminatedThisRound, selectHost, selectRoom } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { clearStandaloneState } from '@/shared/lib/driver';
import { useT } from '@/shared/lib/i18n';
import { localKvStorage } from '@/shared/lib/storage';
import { resolveWinSound } from '../lib/win-sound';
import { RoundResult } from '../ui/RoundResult';

export type RoundResultOrchestratorProps = {
  onFinished: () => void;
  onShowRecap?: () => void;
  recapCount?: number;
};

/** Standalone round-result orchestrator; cleans up storage on `Finished`. */
export const RoundResultOrchestrator = ({
  onFinished,
  onShowRecap,
  recapCount,
}: RoundResultOrchestratorProps) => {
  const t = useT();
  const navigate = useNavigate();
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const host = useGameStore(selectHost);
  const eliminated = useGameStore(selectEliminatedThisRound);
  const driver = useSessionStore((s) => s.driver);
  const beginWordReveal = useGameStore((s) => s.beginWordReveal);
  const resetHandoff = useGameStore((s) => s.resetHandoff);
  const setDriver = useSessionStore((s) => s.setDriver);
  const resetRoom = useGameStore((s) => s.resetRoom);
  const winner = room?.winner ?? null;
  const finished = room?.phase === Phase.Finished;
  const eliminatedThisRound = room?.eliminatedThisRound ?? null;

  // Audio cue at mount: imperative API on the useEffect allow-list.
  useEffect(() => {
    if (!room) return;
    if (finished && winner) {
      const cue = resolveWinSound(winner);
      if (cue) playSound(cue);
      return;
    }
    if (eliminatedThisRound) {
      playSound(SoundCue.PlayerEliminated);
      return;
    }
    playSound(SoundCue.RoundResult);
  }, [room, finished, winner, eliminatedThisRound, playSound]);

  if (!room || !driver) return null;
  const hostId = host?.id ?? room.players[0]?.id;
  if (!hostId) return null;

  const exit = (): void => {
    clearStandaloneState(localKvStorage);
    void driver.stop();
    setDriver(null);
    resetRoom();
    resetHandoff();
  };

  return (
    <RoundResult
      room={room}
      eliminated={eliminated}
      onContinue={() => {
        if (finished) {
          exit();
          onFinished();
          return;
        }
        driver.send({ kind: ClientMsgKind.NextRound, actorPlayerId: hostId });
        beginWordReveal();
      }}
      continueLabel={finished ? t('round.back_home') : t('round.next_round')}
      onRematch={
        finished
          ? () => {
              exit();
              void navigate({ to: '/standalone/setup' });
            }
          : undefined
      }
      onShowRecap={onShowRecap}
      recapCount={recapCount}
    />
  );
};
